'use client'

import { useEffect, useRef } from 'react'

export function HeroIllustration() {
  const svgRef = useRef<SVGSVGElement>(null)

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return

    const winEl = svg.querySelector<SVGGElement>('#hero-win')
    const hexBig = svg.querySelector<SVGGElement>('#hero-hex-big')
    const hexSmall = svg.querySelector<SVGGElement>('#hero-hex-small')
    const sphereEl = svg.querySelector<SVGGElement>('#hero-sphere')

    let rafId = 0
    const onScroll = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const scrollY = window.scrollY
        winEl?.setAttribute('transform', `translate(0,${scrollY * 0.18})`)
        hexBig?.setAttribute('transform', `translate(0,${scrollY * -0.28}) rotate(${scrollY * 0.04},430,105)`)
        hexSmall?.setAttribute('transform', `translate(0,${scrollY * -0.2})`)
        sphereEl?.setAttribute('transform', `translate(0,${scrollY * 0.12})`)
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <div className="relative w-full max-w-xl select-none pointer-events-none">
      <svg
        ref={svgRef}
        viewBox="0 0 580 460"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto"
        aria-hidden
      >
        <defs>
          <radialGradient id="rg-glow" cx="55%" cy="45%" r="50%">
            <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.38" />
            <stop offset="100%" stopColor="#4F46E5" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="rg-sphere" cx="30%" cy="25%" r="75%">
            <stop offset="0%" stopColor="#3730A3" />
            <stop offset="55%" stopColor="#1E1B4B" />
            <stop offset="100%" stopColor="#0A0A14" />
          </radialGradient>
          <linearGradient id="lg-hex-top" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4338CA" />
            <stop offset="100%" stopColor="#1E1B4B" />
          </linearGradient>
          <linearGradient id="lg-hex-side-r" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#2D2B6F" />
            <stop offset="100%" stopColor="#111130" />
          </linearGradient>
          <linearGradient id="lg-hex-side-l" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#252450" />
            <stop offset="100%" stopColor="#0E0E28" />
          </linearGradient>
          <filter id="f-win" x="-15%" y="-15%" width="130%" height="130%">
            <feDropShadow dx="0" dy="14" stdDeviation="22" floodColor="#4F46E5" floodOpacity="0.22" />
          </filter>
          <clipPath id="cp-win">
            <rect x="112" y="102" width="326" height="242" rx="9" />
          </clipPath>
        </defs>

        {/* Ambient glow blob */}
        <ellipse cx="330" cy="228" rx="215" ry="180" fill="url(#rg-glow)" />

        {/* Big 3D hexagonal prism — top right */}
        <g id="hero-hex-big">
          {/* bottom face */}
          <polygon
            points="375,158 403,180 457,180 485,158 457,136 403,136"
            fill="#141430" stroke="#2A2860" strokeWidth="0.75" opacity="0.7"
          />
          {/* right side face */}
          <polygon
            points="457,80 485,102 485,158 457,136"
            fill="url(#lg-hex-side-r)" stroke="#3730A3" strokeWidth="0.5" opacity="0.85"
          />
          {/* left side face */}
          <polygon
            points="375,80 403,58 457,80 457,136 403,136 375,102"
            fill="url(#lg-hex-side-l)" stroke="#3730A3" strokeWidth="0.5" opacity="0.7"
          />
          {/* top face */}
          <polygon
            points="403,58 457,58 485,80 485,102 457,80 403,80"
            fill="url(#lg-hex-top)" stroke="#818CF8" strokeWidth="0.75"
          />
          {/* inner detail lines on top face */}
          <polygon
            points="412,65 448,65 467,80 467,95 448,79 412,79"
            fill="none" stroke="#818CF8" strokeWidth="0.5" opacity="0.4"
          />
          {/* glow top edge */}
          <line x1="403" y1="58" x2="457" y2="58" stroke="#818CF8" strokeWidth="1" opacity="0.5" />
        </g>

        {/* Small hexagon — left side */}
        <g id="hero-hex-small">
          <polygon
            points="80,190 100,178 120,190 120,210 100,222 80,210"
            fill="#1E1B4B" stroke="#4F46E5" strokeWidth="0.75" opacity="0.65"
          />
          <polygon
            points="87,194 100,186 113,194 113,206 100,214 87,206"
            fill="none" stroke="#818CF8" strokeWidth="0.5" opacity="0.45"
          />
        </g>

        {/* Main dashboard window */}
        <g id="hero-win" filter="url(#f-win)">
          {/* Window body */}
          <rect x="112" y="102" width="326" height="242" rx="9" fill="#0C0C1E" stroke="#212135" strokeWidth="1" />

          {/* Title bar */}
          <rect x="112" y="102" width="326" height="36" rx="9" fill="#10102A" />
          <rect x="112" y="120" width="326" height="18" fill="#10102A" />

          {/* Traffic lights */}
          <circle cx="134" cy="120" r="5" fill="#FF5F57" />
          <circle cx="152" cy="120" r="5" fill="#FEBC2E" />
          <circle cx="170" cy="120" r="5" fill="#28C840" />

          {/* Active tab */}
          <rect x="192" y="109" width="70" height="20" rx="4" fill="#1A1A38" />
          {/* Inactive tab */}
          <rect x="270" y="109" width="55" height="20" rx="4" fill="none" stroke="#1E1E3A" strokeWidth="0.75" />

          {/* Window content */}
          <g clipPath="url(#cp-win)">
            <rect x="112" y="138" width="326" height="206" fill="#090917" />

            {/* Left sidebar */}
            <rect x="112" y="138" width="72" height="206" fill="#0C0C22" />
            <line x1="184" y1="138" x2="184" y2="344" stroke="#181830" strokeWidth="1" />

            {/* Sidebar nav items */}
            <rect x="120" y="153" width="52" height="7" rx="2" fill="#1A1A38" />
            <rect x="120" y="170" width="52" height="7" rx="2" fill="#3730A3" fillOpacity="0.55" />
            <rect x="120" y="187" width="52" height="7" rx="2" fill="#1A1A38" />
            <rect x="120" y="204" width="52" height="7" rx="2" fill="#1A1A38" />
            <rect x="120" y="221" width="52" height="7" rx="2" fill="#1A1A38" />
            <rect x="120" y="238" width="40" height="7" rx="2" fill="#1A1A38" />
            {/* Active sidebar indicator */}
            <rect x="112" y="167" width="3" height="13" rx="1.5" fill="#818CF8" />

            {/* Stat cards */}
            <rect x="197" y="148" width="68" height="40" rx="4" fill="#0F0F28" stroke="#1C1C38" strokeWidth="0.75" />
            <rect x="273" y="148" width="68" height="40" rx="4" fill="#0F0F28" stroke="#1C1C38" strokeWidth="0.75" />
            <rect x="349" y="148" width="68" height="40" rx="4" fill="#0F0F28" stroke="#1C1C38" strokeWidth="0.75" />
            {/* Card labels */}
            <rect x="205" y="155" width="28" height="5" rx="2" fill="#242444" />
            <rect x="281" y="155" width="28" height="5" rx="2" fill="#242444" />
            <rect x="357" y="155" width="28" height="5" rx="2" fill="#242444" />
            {/* Card values */}
            <rect x="205" y="166" width="46" height="8" rx="2" fill="#818CF8" fillOpacity="0.75" />
            <rect x="281" y="166" width="36" height="8" rx="2" fill="#34D399" fillOpacity="0.65" />
            <rect x="357" y="166" width="42" height="8" rx="2" fill="#F472B6" fillOpacity="0.55" />

            {/* Chart container */}
            <rect x="197" y="198" width="222" height="82" rx="4" fill="#0B0B20" stroke="#181834" strokeWidth="0.75" />
            {/* Chart grid */}
            <line x1="197" y1="218" x2="419" y2="218" stroke="#181834" strokeWidth="0.5" />
            <line x1="197" y1="238" x2="419" y2="238" stroke="#181834" strokeWidth="0.5" />
            <line x1="197" y1="258" x2="419" y2="258" stroke="#181834" strokeWidth="0.5" />
            {/* Chart fill */}
            <path
              d="M205,268 C218,255 228,262 248,248 S288,232 312,225 S362,215 392,206 L418,202 L418,276 L205,276 Z"
              fill="#4F46E5" fillOpacity="0.13"
            />
            {/* Chart line */}
            <path
              d="M205,268 C218,255 228,262 248,248 S288,232 312,225 S362,215 392,206 L418,202"
              stroke="#818CF8" strokeWidth="1.75" strokeLinecap="round" fill="none"
            />
            {/* Data points */}
            <circle cx="312" cy="225" r="3" fill="#818CF8" />
            <circle cx="312" cy="225" r="5.5" fill="#818CF8" fillOpacity="0.18" />
            <circle cx="392" cy="206" r="3" fill="#818CF8" />
            {/* Tooltip line */}
            <line x1="312" y1="198" x2="312" y2="276" stroke="#818CF8" strokeWidth="0.5" strokeDasharray="2,3" strokeOpacity="0.35" />

            {/* Bottom list rows */}
            <rect x="197" y="292" width="222" height="7" rx="2" fill="#1A1A38" />
            <rect x="197" y="307" width="175" height="6" rx="2" fill="#121228" stroke="#181834" strokeWidth="0.5" />
            <rect x="197" y="320" width="198" height="6" rx="2" fill="#121228" stroke="#181834" strokeWidth="0.5" />
            <rect x="197" y="333" width="155" height="6" rx="2" fill="#121228" stroke="#181834" strokeWidth="0.5" />
          </g>

          {/* Window frame overlay */}
          <rect x="112" y="102" width="326" height="242" rx="9" fill="none" stroke="#262640" strokeWidth="1" />
        </g>

        {/* Sphere — bottom left */}
        <g id="hero-sphere">
          <circle cx="115" cy="372" r="58" fill="url(#rg-sphere)" stroke="#312E81" strokeWidth="0.75" strokeOpacity="0.5" />
          {/* Rim highlight */}
          <path d="M86,335 Q70,358 77,384" stroke="#818CF8" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.28" fill="none" />
          {/* Inner glow spot */}
          <circle cx="96" cy="348" r="22" fill="#4F46E5" fillOpacity="0.07" />
        </g>

        {/* Decorative ambient dots */}
        <circle cx="90" cy="138" r="1.8" fill="#818CF8" fillOpacity="0.45" />
        <circle cx="104" cy="148" r="1.3" fill="#6366F1" fillOpacity="0.35" />
        <circle cx="472" cy="298" r="1.8" fill="#818CF8" fillOpacity="0.45" />
        <circle cx="488" cy="287" r="1.3" fill="#818CF8" fillOpacity="0.3" />
        <circle cx="208" cy="392" r="1.8" fill="#6366F1" fillOpacity="0.4" />
        <circle cx="458" cy="305" r="1.2" fill="#818CF8" fillOpacity="0.3" />

        {/* Subtle dashed connection lines */}
        <line x1="170" y1="334" x2="174" y2="356" stroke="#818CF8" strokeWidth="0.5" strokeDasharray="2,3" strokeOpacity="0.2" />
        <line x1="450" y1="174" x2="446" y2="196" stroke="#6366F1" strokeWidth="0.5" strokeDasharray="2,3" strokeOpacity="0.22" />
      </svg>
    </div>
  )
}
